<template>
  <div v-if="showBanner" class="system-status-banner" :class="`banner-${status.mode}`">
    <div class="container-fluid">
      <div class="row align-items-center">
        <div class="col">
          <div class="d-flex align-items-center">
            <i :class="getBannerIcon()" class="me-3 fs-4"></i>
            <div>
              <div class="fw-bold">{{ getBannerTitle() }}</div>
              <div class="small">{{ status.message || getDefaultMessage() }}</div>
            </div>
          </div>
        </div>
        <div class="col-auto">
          <button
            type="button"
            class="btn-close btn-close-white"
            @click="dismissBanner"
            aria-label="Close"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/api/http'

export default {
  name: 'SystemStatusBanner',

  data() {
    return {
      status: {
        mode: 'operational',
        message: null,
        tone: 'friendly',
        enabled_at: null,
        auto_detected: false
      },
      showBanner: false,
      dismissed: false
    }
  },

  mounted() {
    this.checkSystemStatus()
    // Check status every 30 seconds
    this.statusInterval = setInterval(() => {
      this.checkSystemStatus()
    }, 30000)
  },

  beforeUnmount() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval)
    }
  },

  methods: {
    async checkSystemStatus() {
      try {
        // Don't re-check if user dismissed banner
        if (this.dismissed && this.status.mode !== 'operational') {
          return
        }

        const res = await http.get('/api/v1/system/status')
        this.status = res.data

        // Show banner if not operational and not dismissed
        if (this.status.mode !== 'operational' && !this.dismissed) {
          this.showBanner = true
        } else {
          this.showBanner = false
          // Reset dismissed flag when back to operational
          if (this.status.mode === 'operational') {
            this.dismissed = false
          }
        }
      } catch (error) {
        // If we can't reach the API, don't show error banner
        // (avoids recursive error display)
        console.error('Failed to check system status:', error)
      }
    },

    dismissBanner() {
      this.showBanner = false
      this.dismissed = true
    },

    getBannerIcon() {
      if (this.status.mode === 'maintenance') {
        return 'bi bi-wrench-adjustable'
      } else if (this.status.mode === 'degraded') {
        return 'bi bi-exclamation-triangle'
      }
      return 'bi bi-info-circle'
    },

    getBannerTitle() {
      if (this.status.mode === 'maintenance') {
        return this.status.auto_detected ? 'Service Temporarily Unavailable' : 'System Maintenance'
      } else if (this.status.mode === 'degraded') {
        return 'Service Degraded'
      }
      return 'Notice'
    },

    getDefaultMessage() {
      const messages = {
        professional: {
          maintenance: 'The system is currently undergoing maintenance. Please check back shortly.',
          degraded: 'Some services may be experiencing issues. We are working to resolve this.'
        },
        friendly: {
          maintenance: "We're making PAM even better! Check back soon.",
          degraded: "We're experiencing some technical difficulties. Hang tight!"
        },
        humorous: {
          maintenance: "PAM is doing yoga 🧘 (stretching her capabilities). Be right back!",
          degraded: "PAM stubbed her toe. Give us a moment to recover!"
        }
      }

      const tone = this.status.tone || 'friendly'
      const mode = this.status.mode || 'maintenance'

      return messages[tone]?.[mode] || messages.friendly.maintenance
    }
  }
}
</script>

<style scoped>
.system-status-banner {
  padding: 1rem 0;
  color: white;
  position: sticky;
  top: 0;
  z-index: 1030;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.banner-maintenance {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.banner-degraded {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.btn-close-white {
  opacity: 0.8;
}

.btn-close-white:hover {
  opacity: 1;
}

/* Dark mode support */
[data-bs-theme="dark"] .system-status-banner {
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
</style>
